import { useRef, useCallback } from 'react';

/**
 * Custom hook for debounced form submissions
 * Prevents multiple submissions within a specified delay period
 * 
 * @param {number} delay - Debounce delay in milliseconds (default: 500ms)
 * @returns {Object} - { canSubmit, handleSubmit }
 */
export const useFormSubmit = (delay = 500) => {
    const lastSubmitTime = useRef(0);
    const isSubmittingRef = useRef(false);

    /**
     * Wraps a submit function with debouncing logic
     * @param {Function} submitFn - The async submit function to execute
     * @returns {Promise<boolean>} - Returns true if submitted, false if debounced
     */
    const handleSubmit = useCallback(async (submitFn) => {
        const now = Date.now();
        const timeSinceLastSubmit = now - lastSubmitTime.current;

        // Check if we're within the debounce window
        if (timeSinceLastSubmit < delay) {
            console.log(`Submission debounced (${timeSinceLastSubmit}ms since last submit)`);
            return false;
        }

        // Check if already submitting
        if (isSubmittingRef.current) {
            console.log('Submission already in progress');
            return false;
        }

        // Update last submit time and set submitting flag
        lastSubmitTime.current = now;
        isSubmittingRef.current = true;

        try {
            await submitFn();
            return true;
        } catch (error) {
            console.error('Submit error:', error);
            throw error;
        } finally {
            isSubmittingRef.current = false;
        }
    }, [delay]);

    /**
     * Check if submission is allowed (not within debounce window)
     */
    const canSubmit = useCallback(() => {
        const now = Date.now();
        const timeSinceLastSubmit = now - lastSubmitTime.current;
        return timeSinceLastSubmit >= delay && !isSubmittingRef.current;
    }, [delay]);

    return { canSubmit, handleSubmit };
};
